import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getNonDraftedOffers } from "../../../services/offerService";
import { ReactComponent as DeleteIcon } from "../../../Assets/Icons/deleteIcon.svg";
import { ReactComponent as MapIcon } from "../../../Assets/Icons/MapIcon.svg";
import { ReactComponent as MailIcon } from "../../../Assets/Icons/mailIcon.svg";
import { ReactComponent as WebIcon } from "../../../Assets/Icons/webIcon.svg";
import { ReactComponent as UserIcon } from "../../../Assets/Icons/userIcon.svg";
import { ReactComponent as PhoneIcon } from "../../../Assets/Icons/phoneIcon.svg";
import "../../common/OfferList/offerList.scss";
import defaultCategoryIcon from "../../../Assets/Icons/subCategoryIcon.svg";
import Confirmation from "../../common/OfferList/Confirmation";
import StoresTable from "./StoresTable";
import { Dropdown } from "antd";
import eyeIcon from "../../../Assets/Icons/eyeviewicon.svg";



const PopupContent = ({ merchantDetails, onDelete, onApprove, onDecline, onReviewStatus, onShowReviewModal, onChangeLogo, onEdit, onStatusChangeRequest, onAddLocation,
  onUploadCSV,
  onEditLocation
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const role = useSelector((state) => state?.authReducer?.tokenData?.user?.role);
  const [offerCount, setOfferCount] = React.useState(merchantDetails?.offersCount || 0);

  React.useEffect(() => {
    const fetchOfferCount = async () => {
      if (!merchantDetails?._id) return;
      try {
        const response = await dispatch(getNonDraftedOffers({
          merchantId: merchantDetails._id,
          status: 'approved',
          limit: 1,
          silent: true
        })).unwrap();
        if (response?.success) {
          const count = response?.total || response?.totalCount || response?.count || (Array.isArray(response?.data) ? response?.data?.length : 0);
          setOfferCount(count);
        }
      } catch (error) {
        console.error("Error fetching offer count:", error);
      }
    };

    if (merchantDetails?._id && !merchantDetails?.offersCount) {
      fetchOfferCount();
    } else if (merchantDetails?.offersCount) {
      setOfferCount(merchantDetails.offersCount);
    }
  }, [merchantDetails?._id, merchantDetails?.offersCount, dispatch]);

  const {
    address,
    businessName,
    category,
    contactPersonName,
    contactPersonNumber,
    logo,
    user,
    website,
    _id,
    status,
    description,
    stores,
    // Fallback fields for in-process merchants
    email,
    phoneNumber,
    phone,
    contact,
    mobile,
    location,
    businessAddress,
    business_address,
    websiteUrl,
    url,
    contactName,
    name,
    aboutUs,
    aboutBusiness
  } = merchantDetails || {}; // Safety check

  // Derived values to handle both data structures
  const displayEmail = user?.email || email || "N/A";
  const displayContact = contactPersonNumber || phoneNumber || phone || contact || mobile || "N/A";
  const displayAddress = address || location || businessAddress || business_address || "N/A";
  const displayWebsite = website || websiteUrl || url || "N/A";
  const displayContactName = contactPersonName || contactName || name || user?.name || "N/A";

  // Display all assigned stores exactly as returned by the API
  const displayStores = stores || [];

  const sortedStores = [...displayStores].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  if (!merchantDetails) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="loader"></div>
        <p className="mt-4 text-[#767676] font-poppins text-[15px]">Loading merchant details...</p>
      </div>
    );
  }

  return (
    <div
      style={{ scrollbarWidth: "none" }}
      className="max-h-[85vh] overflow-y-auto w-full md:w-auto min-w-[300px] flex flex-col gap-4 font-poppins scrollbar-hidden pb-12"
    >
      <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4 sm:gap-6">
        <div className="flex justify-start items-center gap-4 flex-1 min-w-0 w-full sm:w-auto">
          <div className="relative flex-shrink-0">
            {(function () {
              const l = logo;
              const isMissing = !l || (typeof l === 'string' && l.trim() === '');
              const src = (function () {
                if (isMissing) return require("../../../Assets/Images/PersonBlue.svg").default;
                const isAbs = typeof l === 'string' && /^(https?:)?\/\//i.test(l);
                const baseUrl = isAbs ? l : `${process.env.REACT_APP_S3_URL || ''}${l}`;
                // Append timestamp to bust cache when logo is updated
                return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
              })();
              if (isMissing) {
                return (
                  <>
                    <div className="w-[60px] h-[60px] md:w-[72px] md:h-[72px] rounded-full bg-[#E6F7FF] border border-[#00ADEF33] flex items-center justify-center flex-shrink-0">
                      <img src={src} alt="merchantLogo" className="w-[38px] h-[38px] md:w-[44px] md:h-[44px] object-contain" />
                    </div>
                    {(role === "admin" || role === "ADMIN") && onChangeLogo && (
                      <button
                        onClick={() => onChangeLogo(merchantDetails)}
                        className="absolute bottom-0 right-0 w-[20px] h-[20px] md:w-[24px] md:h-[24px] rounded-full bg-[#00ADEF] flex items-center justify-center border-2 border-white shadow-md hover:bg-[#0088CC] transition-colors"
                        title="Change Logo"
                      >
                        <img
                          src={require("../../../Assets/Icons/editIcon.svg").default}
                          alt="Edit"
                          className="w-[10px] h-[10px] md:w-[12px] md:h-[12px] filter brightness-0 invert"
                        />
                      </button>
                    )}
                  </>
                );
              }
              return (
                <>
                  <div
                    className="w-[60px] h-[60px] md:w-[72px] md:h-[72px] rounded-full bg-white border border-[#00ADEF33] flex items-center justify-center flex-shrink-0 overflow-hidden"
                  >
                    <img
                      src={src}
                      alt="merchantLogo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  {(role === "admin" || role === "ADMIN") && onChangeLogo && (
                    <button
                      onClick={() => onChangeLogo(merchantDetails)}
                      className="absolute bottom-0 right-0 w-[20px] h-[20px] md:w-[24px] md:h-[24px] rounded-full bg-[#00ADEF] flex items-center justify-center border-2 border-white shadow-md hover:bg-[#0088CC] transition-colors"
                      title="Change Logo"
                    >
                      <img
                        src={require("../../../Assets/Icons/editIcon.svg").default}
                        alt="Edit"
                        className="w-[10px] h-[10px] md:w-[12px] md:h-[12px] filter brightness-0 invert"
                      />
                    </button>
                  )}
                </>
              );
            })()}
          </div>

          <div className="flex items-center gap-2 max-w-full">
            <h1 className="font-[600] text-[16px] md:text-[18px] lg:text-[20px] truncate max-w-[200px] sm:max-w-[250px] md:max-w-[350px]">
              {businessName}
            </h1>
          </div>
        </div>
        <div className="flex flex-col items-end gap-3 flex-shrink-0 mt-2 sm:mt-0 sm:ml-auto">
          {/* Status Badge at the top right */}
          <div className="flex items-center gap-1">
            {(role === "admin" || role === "ADMIN") && (status?.toLowerCase() === "approved" || status?.toLowerCase() === "declined" || status?.toLowerCase() === "rejected") ? (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: '1',
                      label: (
                        <div className="flex items-center gap-2 pr-6 pl-1 py-1">
                          <div
                            className="flex items-center justify-center w-[16px] h-[16px] rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: status?.toLowerCase() === 'approved' ? '#EC826E' : '#50CD89'
                            }}
                          >
                            {status?.toLowerCase() === 'approved' ? (
                              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ display: 'block' }}>
                                <path d="M6 2L2 6M2 2L6 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                              </svg>
                            ) : (
                              <svg width="8" height="6" viewBox="0 0 8 6" fill="none" style={{ display: 'block' }}>
                                <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                          <span
                            className={`text-[13px] font-[600] font-poppins`}
                            style={{
                              color: status?.toLowerCase() === 'approved' ? '#EC826E' : '#4CAF50'
                            }}
                          >
                            {status?.toLowerCase() === 'approved' ? 'Declined' : 'Approved'}
                          </span>
                        </div>
                      ),
                      onClick: () => onStatusChangeRequest && onStatusChangeRequest(status?.toLowerCase() === 'approved' ? 'declined' : 'approved')
                    }
                  ]
                }}
                trigger={['click']}
                placement="bottomLeft"
              >
                <div
                  className="cursor-pointer flex items-center h-[30px] px-2.5 rounded-full border-[0.5px] transition-all hover:opacity-90 active:scale-[0.98] select-none"
                  style={{
                    backgroundColor: status?.toLowerCase() === 'approved' ? 'rgba(80, 205, 137, 0.15)' : 'rgba(236, 130, 110, 0.15)',
                    borderColor: status?.toLowerCase() === 'approved' ? '#50CD89' : '#EC826E',
                    minWidth: '105px'
                  }}
                >
                  <div
                    className="flex items-center justify-center w-[16px] h-[16px] rounded-full flex-shrink-0"
                    style={{
                      backgroundColor: status?.toLowerCase() === 'approved' ? '#50CD89' : '#EC826E'
                    }}
                  >
                    {status?.toLowerCase() === 'approved' ? (
                      <svg width="8" height="6" viewBox="0 0 8 6" fill="none" style={{ display: 'block' }}>
                        <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ display: 'block' }}>
                        <path d="M6 2L2 6M2 2L6 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    )}
                  </div>

                  <span
                    className="text-[12.5px] font-[600] ml-2 font-poppins capitalize"
                    style={{
                      color: status?.toLowerCase() === 'approved' ? '#50CD89' : '#EC826E'
                    }}
                  >
                    {status}
                  </span>

                  <div className="ml-auto pl-1 flex items-center">
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                      <path d="M1 1.5L5 5.5L9 1.5" stroke={status?.toLowerCase() === 'approved' ? '#50CD89' : '#EC826E'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </Dropdown>
            ) : (
              <Confirmation status={status} />
            )}
          </div>

          {/* Edit Profile Button below the Status Badge */}
          <div className="flex items-center gap-2">
            {(role === "admin" || role === "ADMIN") && onEdit && (
              <button
                type="button"
                onClick={() => onEdit(merchantDetails)}
                className="px-6 py-2 text-[13px] font-[600] bg-[#00ADEF] text-white rounded-[6px] hover:bg-[#0088CC] transition-colors whitespace-nowrap shadow-sm"
              >
                Edit Profile
              </button>
            )}
            {status === "pending" && (
              <button
                type="button"
                title="Delete Merchant"
                onClick={() => onDelete && onDelete(_id)}
                className="p-2 rounded hover:bg-[#F5F5F5] focus:outline-none"
              >
                <DeleteIcon />
              </button>
            )}
          </div>
        </div>

      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {Array.isArray(category) && category.length > 0 && category.map((c, index) => {
          const title = typeof c === 'string' ? c : (c?.title || '');

          // Check if title looks like a Mongo ID (24 hex chars)
          // If it is, we skip rendering it because the user doesn't want to see IDs
          if (/^[0-9a-fA-F]{24}$/.test(title)) {
            return null;
          }

          const imgPath = typeof c === 'string' ? (c?.image || '') : (c?.image || '');
          const src = (function () {
            const url = imgPath;
            const isAbsolute = typeof url === 'string' && /^(https?:)?\/\//i.test(url);
            if (isAbsolute && url) return url;
            const baseUrl = process.env.REACT_APP_ICON_URL || '';
            if (url) {
              // ensure exactly one slash between base and path
              const needsSlash = baseUrl && !baseUrl.endsWith('/') && url && !url.startsWith('/');
              return `${baseUrl}${needsSlash ? '/' : ''}${url}`;
            }
            // fallback to local default icon
            return defaultCategoryIcon;
          })();
          return (
            <div key={`merchant-cat-${index}-${title}`} className="flex justify-start items-center gap-3">
              <img
                src={src}
                alt="categoryImg"
                className="w-[22px] h-[22px] filter-blue"
              />
              <h3 className="text-[#303030] text-[14px]">{title}</h3>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-2">
        <div className="flex gap-2 items-center">
          <MailIcon />
          <p className="text-[14px] font-[400] text-[#767676]">{displayEmail}</p>
        </div>
        <div className="flex gap-2 items-center">
          <WebIcon />
          <p className="text-[14px] font-[400] text-[#767676]">{displayWebsite}</p>
        </div>
        <div className="flex gap-2 items-center">
          <UserIcon />
          <p className="text-[14px] font-[400] text-[#767676]">{displayContactName}</p>
        </div>
        <div className="flex gap-2 items-center">
          <PhoneIcon />
          <p className="text-[14px] font-[400] text-[#767676]">{displayContact}</p>
        </div>
      </div>
      <div className="flex mt-1 justify-start items-center gap-2 text-[14px] font-[400] text-[#767676]">
        <MapIcon />
        <p>{displayAddress}</p>
      </div>



      <div className="w-full flex flex-col mt-2.5 gap-1 items-start font-poppins">
        <h2 className="text-[15px] font-[600] text-[#000000] text-start">
          Description:
        </h2>
        <p className="w-full text-start text-[13px] font-[400] text-[#767676] leading-relaxed">
          {aboutUs || description || aboutBusiness || "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."}
        </p>
      </div>

      {/* Offer Count Section */}
      <div className="flex flex-col mt-2 w-full items-start">
        <div className="flex items-center gap-1.5">
          <h2 className="text-[15px] font-[600] text-[#303030] font-poppins text-start">
            Offer Count ({offerCount})
          </h2>
          <button
            type="button"
            className="flex items-center justify-center border-none bg-transparent hover:opacity-80 transition-opacity -mt-[2px]"
            onClick={() => {
              navigate(`/offer-list?search=${encodeURIComponent(businessName)}&merchantId=${merchantDetails?._id}`);
            }}
            title="View Offers"
          >
            <img
              src={eyeIcon}
              alt="View Offers"
              className="w-[20px] h-[20px] cursor-pointer"
              style={{ filter: "invert(50%) sepia(85%) saturate(1800%) hue-rotate(175deg) brightness(100%) contrast(100%)" }}
            />
          </button>
        </div>
      </div>

      {/* Merchant Store Locations Card Section */}
      <div className="flex flex-col mt-1 w-full items-start">
        <h2 className="text-[15px] font-[600] text-[#303030] mb-2 font-poppins text-start">
          Merchant store locations ({displayStores.length}):
        </h2>

        {/* Scrollable Locations Container */}
        <div
          className="bg-white rounded-[10px] p-4 flex flex-col gap-3 overflow-y-auto scrollbar-hidden w-[98%] mx-auto"
          style={{
            maxHeight: '240px',
            boxShadow: '0px 4px 30px 0px rgba(121, 121, 121, 0.15)'
          }}
        >
          {sortedStores.length > 0 ? (
            sortedStores.map((store, index) => (
              <div
                key={index}
                className="flex justify-between items-center py-2.5 px-4 bg-white border border-[#E0E0E0] rounded-[8px] hover:border-[#00ADEF66] transition-all shadow-sm group"
              >
                <div className="flex items-center gap-3 overflow-hidden">
                  <span className="text-[13px] text-[#424242] font-poppins truncate leading-5">
                    {store.address || store.location || store.googlePlaceId?.address}
                  </span>
                </div>
                <button
                  onClick={() => onEditLocation && onEditLocation(store)}
                  className="text-[13px] text-[#00ADEF] font-[600] hover:text-[#0088CC] transition-colors ml-4 flex-shrink-0"
                >
                  Edit
                </button>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <p className="text-[#767676] text-[13px] font-poppins">No store location added yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Store Location Action Buttons */}
      <div className="flex flex-col mt-6 gap-3 items-start relative z-50">
        <h3 className="text-[14px] font-[600] text-[#303030] font-poppins">Add Store Location</h3>
        <div className="flex flex-wrap gap-4 w-full">
          <button
            type="button"
            onClick={() => onAddLocation && onAddLocation()}
            className="flex items-center gap-2.5 bg-[#00ADEF] text-white px-6 py-3 rounded-[8px] text-[13px] font-[700] hover:bg-[#0088CC] transition-all shadow-md active:scale-[0.95] cursor-pointer"
          >
            <MapIcon className="w-[18px] h-[18px] filter-white" />
            Add a New Store Location
          </button>

          <button
            type="button"
            onClick={() => onUploadCSV && onUploadCSV()}
            className="flex items-center gap-2.5 bg-[#00ADEF] text-white px-6 py-3 rounded-[8px] text-[13px] font-[700] hover:bg-[#0088CC] transition-all shadow-md active:scale-[0.95] cursor-pointer"
          >
            <img src={require("../../../Assets/Icons/csvIcon.svg").default} alt="CSV" className="w-[18px] h-[18px] filter-white" />
            Upload Via CSV
          </button>
        </div>
      </div>

      {status === "pending" && (
        <div className="w-full flex justify-end items-center gap-3 mt-8 border-t border-[#F0F0F0] pt-6">
          <button
            type="button"
            onClick={() => onDecline && onDecline(_id)}
            className="border border-[#00ADEF] text-[#00ADEF] h-[38px] px-6 py-1 rounded-[6px] text-[13px] font-[600] whitespace-nowrap hover:bg-[#F0FBFF] transition-colors"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={() => onApprove && onApprove(_id)}
            className="bg-[#00ADEF] text-white h-[38px] whitespace-nowrap px-8 py-1 rounded-[6px] text-[13px] font-[600] hover:bg-[#0088CC] transition-colors"
          >
            Approve
          </button>
        </div>
      )}


    </div>
  );
};

export default PopupContent;
